import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, MinusIcon } from '@heroicons/react/24/outline';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Card } from './Card';
import { Inline, Stack } from './Layout';

// ------------------------------------------------------------------
// MetricCard — story 2-147ta (P10 §2.7 row 5 "Product/data cards", DOD).
// The canonical dashboard stat/metric-card shape — a label, a big number,
// an optional trend delta — composed OF `Card` rather than a fourth
// independent implementation.
//
// FORMALIZES, DOES NOT REPLACE, `KpiCard`. P10 §2.7 row 5 already
// investigated `KpiCard.tsx` (`components/dashboard`) against the rule-of-
// three promotion bar (`2-147d`) and settled it stays module-scoped,
// unpromoted, "until a real second module reaches for the same generic
// stat/metric-card shape independently" — that scoping call is NOT
// re-litigated here (P10 §2.7's own text: "not re-litigated"). What IS this
// story's job: `KpiCard`'s own `rounded-hairline border border-border
// bg-card ... p-4` wrapper is exactly the locally-recreated card contract
// this story's done-when measures — so `KpiCard.tsx` now composes THIS
// component internally instead of re-deriving the border/padding/radius by
// hand (a judgment call, recorded in SESSION-HANDOFF.md: this is an
// internal-implementation refactor, not a promotion — `KpiCard`'s own
// location, name and props are unchanged, so DashboardPage.tsx needed zero
// changes).
// ------------------------------------------------------------------

export type MetricCardTrend = 'up' | 'down' | 'flat';

const TREND_ICON: Record<MetricCardTrend, ComponentType<SVGProps<SVGSVGElement>>> = {
  up: ArrowTrendingUpIcon,
  down: ArrowTrendingDownIcon,
  flat: MinusIcon,
};

// Trend colour is never the ONLY carrier of meaning — the `delta` text
// itself already states the direction/number (e.g. "+12%"), matching
// `TaskCard.tsx`'s own "the accent is never the only carrier of meaning"
// rule (WCAG 1.4.1). `success`/`destructive` are the same two roles
// `contrast-check.mjs` verifies at AA in both themes; `flat` stays neutral
// `text-muted-foreground` rather than either.
const TREND_TEXT_CLASS: Record<MetricCardTrend, string> = {
  up: 'text-success',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
};

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  /** The delta itself, as text (e.g. `'+12%'`) — required alongside `trend` so direction is never colour-only. */
  delta?: string;
  /** Defaults to `'up'` — matches `KpiCard`'s own prior implicit assumption (every real consumer today is a positive trend). */
  trend?: MetricCardTrend;
  trendLabel?: string;
  icon?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function MetricCard({
  label,
  value,
  delta,
  trend = 'up',
  trendLabel,
  icon,
  className = '',
  'data-testid': testId,
}: MetricCardProps): JSX.Element {
  const TrendIcon = TREND_ICON[trend];
  return (
    <Card className={className} {...(testId ? { 'data-testid': testId } : {})}>
      {/* `Inline` (flex-nowrap), not `Cluster` (flex-wrap) — this header row
          is a label+value on one side and exactly one icon badge on the
          other, never more than that, so it must never wrap: a long label
          ("Active inspections" vs "Buildings"/"Reports") combined with a
          narrower grid column made `Cluster` wrap the icon onto its own
          line below the value instead of truncating the label, which the
          `min-w-0`/`truncate` below were already set up to do — a real bug
          found live (the wrapped icon looked like a second, unexplained
          icon under the number). */}
      <Inline align="start" gap={4} className="justify-between">
        <Stack gap={1} className="min-w-0">
          <p className="truncate text-xs uppercase tracking-eyebrow text-muted-foreground">{label}</p>
          <p className="text-3xl font-semibold text-foreground">{value}</p>
        </Stack>
        <span className="shrink-0 rounded-full bg-muted p-2 text-primary" aria-hidden="true">
          {icon ?? <ArrowTrendingUpIcon className="h-5 w-5" />}
        </span>
      </Inline>
      {delta != null && (
        <Inline gap={2} className="text-sm">
          <Inline gap={1} className={`font-medium ${TREND_TEXT_CLASS[trend]}`}>
            <TrendIcon className="h-4 w-4" aria-hidden="true" />
            <span>{delta}</span>
          </Inline>
          <span className="text-muted-foreground">{trendLabel ?? 'vs last period'}</span>
        </Inline>
      )}
    </Card>
  );
}
